import React, { useEffect, useState } from "react";
import { useProjects } from "../context/ProjectContext";
import api, { apiError } from "../lib/api";
import { NoProject } from "../components/EmptyState";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Upload, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle, Loader2, ArrowRight, ArrowLeft, X,
} from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  ["setup", "Budget & Cost-Code Setup"],
  ["changes", "Changes"],
  ["actuals", "Weekly Installed Qty & Labor Hours"],
];
const FIELDS = {
  setup: ["cost_code", "description", "uom", "labor_class", "standard_labor_rate", "original_budget_qty", "original_budget_hours"],
  changes: ["date", "change_id", "cost_code", "description", "status", "qty_adjustment", "hour_adjustment", "include_in_forecast", "notes"],
  actuals: ["week_ending", "cost_code", "installed_qty", "st_hours", "ot_hours", "notes", "source_record_id"],
};

export default function ImportWizard() {
  const { selected } = useProjects();
  const [importType, setImportType] = useState("setup");
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [acc, setAcc] = useState([]);
  const [rej, setRej] = useState([]);

  const loadHistory = () => selected && api.get(`/projects/${selected.id}/imports`).then((r) => setHistory(r.data));
  useEffect(() => { loadHistory(); }, [selected]);

  useEffect(() => {
    if (preview) {
      setAcc(preview.accepted || []);
      setRej((preview.rejected || []).map((r) => ({ row: r.row, fields: { ...(r.data || {}) }, errors: r.errors })));
    }
  }, [preview]);

  const editRej = (i, field, val) =>
    setRej((prev) => prev.map((r, idx) => (idx === i ? { ...r, fields: { ...r.fields, [field]: val } } : r)));

  const revalidateFixed = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/projects/${selected.id}/imports/validate-rows`, {
        import_type: importType, rows: rej.map((r) => r.fields),
      });
      const fixed = data.accepted.length;
      setAcc((prev) => [...prev, ...data.accepted]);
      setRej(data.rejected.map((r) => ({ row: r.row, fields: { ...r.data }, errors: r.errors })));
      toast.success(fixed > 0 ? `${fixed} row(s) fixed and moved to accepted.` : "No rows passed validation yet.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (!selected) return <NoProject />;

  const doPreview = async (f) => {
    setLoading(true);
    const fd = new FormData();
    fd.append("import_type", importType);
    fd.append("file", f);
    try {
      const { data } = await api.post(`/projects/${selected.id}/imports/preview`, fd);
      setPreview(data); setMapping(data.mapping || {}); setStep(2);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const revalidate = async () => {
    // re-run preview with adjusted mapping by re-uploading file
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("import_type", importType);
    fd.append("file", file);
    try {
      const { data } = await api.post(`/projects/${selected.id}/imports/preview`, fd);
      // apply current manual mapping over server result by rebuilding client-side not available;
      // simplest: send mapping to preview isn't supported, so just move to step 3 with server data
      setPreview({ ...data, mapping });
      setStep(3);
    } finally { setLoading(false); }
  };

  const commit = async () => {
    setLoading(true);
    try {
      const errs = rej.map((r) => `Row ${r.row}: ${r.errors.join("; ")}`);
      const { data } = await api.post(`/projects/${selected.id}/imports/commit`, {
        import_type: importType, filename: preview.filename, mapping,
        accepted: acc, rejected_count: rej.length,
        validation_errors: errs, save_mapping: true,
      });
      toast.success(`Imported ${data.inserted} row(s). ${data.skipped_duplicate} duplicate(s) skipped.`);
      reset(); loadHistory();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const reset = () => { setStep(1); setFile(null); setPreview(null); setMapping({}); };

  return (
    <div className="space-y-4" data-testid="import-page">
      <h1 className="font-head text-2xl font-bold text-slate-900">Import Data</h1>

      {/* Stepper */}
      <div className="flex items-center gap-2 text-sm">
        {["Upload", "Map Columns", "Preview & Confirm"].map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${step === i + 1 ? "bg-[#0B192C] text-white" : "bg-slate-100 text-slate-500"}`}>
              <span className="font-mono">{i + 1}</span>{s}
            </div>
            {i < 2 && <ArrowRight className="w-4 h-4 text-slate-300" />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm">
        {step === 1 && (
          <div className="space-y-4" data-testid="import-step-upload">
            <div className="max-w-sm">
              <label className="text-xs font-medium text-slate-600">Import Type</label>
              <Select value={importType} onValueChange={setImportType}>
                <SelectTrigger className="mt-1" data-testid="import-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {importType === "actuals" && (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 flex gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                Weekly actuals must be <b>incremental per period</b>. Do not upload cumulative-to-date quantities repeatedly.
              </div>
            )}
            <label className="block border-2 border-dashed border-slate-300 rounded-lg p-10 text-center cursor-pointer hover:border-[#E8791A] transition-colors"
                   data-testid="import-upload-zone">
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <div className="text-sm text-slate-600">{file ? file.name : "Drop a CSV or Excel file, or click to browse"}</div>
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden"
                     onChange={(e) => { const f = e.target.files[0]; if (f) { setFile(f); doPreview(f); } }}
                     data-testid="import-file-input" />
            </label>
            {loading && <div className="flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0B192C]" /></div>}
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-4" data-testid="import-step-map">
            <p className="text-sm text-slate-600">Map your file columns to application fields. Detected columns: {preview.columns.length}.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {FIELDS[importType].map((field) => (
                <div key={field} className="flex items-center gap-2">
                  <span className="text-sm font-mono w-44 text-slate-700">{field}</span>
                  <Select value={mapping[field] || "__none"} onValueChange={(v) => setMapping({ ...mapping, [field]: v === "__none" ? undefined : v })}>
                    <SelectTrigger className="h-9 flex-1" data-testid={`map-${field}`}><SelectValue placeholder="— none —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">— none —</SelectItem>
                      {preview.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
              <Button onClick={revalidate} disabled={loading} className="bg-[#0B192C]" data-testid="import-to-preview">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Validate & Preview<ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && preview && (
          <div className="space-y-4" data-testid="import-step-preview">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Stat icon={CheckCircle2} color="text-emerald-600" label="Accepted" value={acc.length} testid="accepted-count" />
              <Stat icon={XCircle} color="text-red-600" label="Rejected" value={rej.length} testid="rejected-count" />
              <Stat icon={AlertTriangle} color="text-amber-600" label="Warnings" value={preview.warnings.length} testid="warnings-count" />
            </div>

            {preview.cumulative_warning && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 flex gap-2" data-testid="cumulative-warning">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                This file looks <b>cumulative</b> (steadily increasing quantities per cost code). Confirm the quantities are incremental per period before saving.
              </div>
            )}

            {preview.warnings.length > 0 && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 max-h-32 overflow-y-auto">
                {preview.warnings.map((w, i) => <div key={i}>{w}</div>)}
              </div>
            )}

            {rej.length > 0 && (
              <div className="border border-red-200 rounded-md" data-testid="rejected-editor">
                <div className="flex items-center justify-between bg-red-50 px-3 py-2 border-b border-red-200">
                  <span className="text-sm font-semibold text-red-800">{rej.length} rejected row(s) — fix a value inline, then re-validate, or exclude the row</span>
                  <Button size="sm" onClick={revalidateFixed} disabled={loading} className="bg-[#0B192C] hover:bg-[#142843]" data-testid="revalidate-button">
                    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Re-validate fixed rows
                  </Button>
                </div>
                <div className="overflow-x-auto max-h-64">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50"><tr>
                      <th className="px-2 py-1.5 text-left font-semibold text-slate-500">#</th>
                      {FIELDS[importType].map((f) => <th key={f} className="px-2 py-1.5 text-left font-semibold text-slate-600">{f}</th>)}
                      <th className="px-2 py-1.5 text-left font-semibold text-red-600">Errors</th><th></th>
                    </tr></thead>
                    <tbody>
                      {rej.map((r, i) => (
                        <tr key={i} className="border-t border-slate-100" data-testid={`rejected-row-${i}`}>
                          <td className="px-2 py-1 font-mono text-slate-400">{r.row}</td>
                          {FIELDS[importType].map((f) => (
                            <td key={f} className="px-1 py-1">
                              <input value={r.fields[f] ?? ""} onChange={(e) => editRej(i, f, e.target.value)}
                                     className="w-24 border border-slate-200 rounded px-1 py-0.5 font-mono text-xs focus:border-[#E8791A] outline-none"
                                     data-testid={`rej-input-${i}-${f}`} />
                            </td>
                          ))}
                          <td className="px-2 py-1 text-red-600 max-w-[180px] truncate" title={r.errors.join("; ")}>{r.errors.join("; ")}</td>
                          <td className="px-2 py-1">
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => setRej(rej.filter((_, idx) => idx !== i))} data-testid={`exclude-rejected-${i}`}>
                              <X className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Rows to import ({acc.length})</div>
            <div className="overflow-x-auto border border-slate-200 rounded max-h-64">
              <table className="w-full text-xs">
                <thead className="bg-slate-50"><tr>
                  {FIELDS[importType].map((f) => <th key={f} className="px-2 py-1.5 text-left font-semibold text-slate-600">{f}</th>)}
                  <th></th>
                </tr></thead>
                <tbody>
                  {acc.length === 0 ? (
                    <tr><td colSpan={FIELDS[importType].length + 1} className="px-2 py-6 text-center text-slate-400">No rows selected for import.</td></tr>
                  ) : acc.slice(0, 100).map((r, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`accepted-row-${i}`}>
                      {FIELDS[importType].map((f) => <td key={f} className="px-2 py-1 font-mono">{String(r[f] ?? "")}</td>)}
                      <td className="px-2 py-1">
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-400 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setAcc(acc.filter((_, idx) => idx !== i))} data-testid={`exclude-accepted-${i}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)} className="hover:bg-slate-900 hover:text-white"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
              <Button onClick={commit} disabled={loading || acc.length === 0} className="bg-[#E8791A] hover:bg-[#D66A0F]" data-testid="import-commit-button">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Confirm & Import {acc.length} rows
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* History */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm">
        <h3 className="font-head font-semibold p-3 border-b border-slate-100">Import History</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="import-history-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600"><tr>
              <th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">File</th>
              <th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2 text-left">By</th>
              <th className="px-3 py-2 text-right">Accepted</th><th className="px-3 py-2 text-right">Dupes</th>
              <th className="px-3 py-2 text-right">Rejected</th>
            </tr></thead>
            <tbody>
              {history.length === 0 ? <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">No imports yet.</td></tr> :
                history.map((h) => (
                  <tr key={h.id} className="border-t border-slate-100">
                    <td className="px-3 py-2">{new Date(h.import_date).toLocaleString()}</td>
                    <td className="px-3 py-2">{h.filename}</td>
                    <td className="px-3 py-2">{h.import_type}</td>
                    <td className="px-3 py-2">{h.imported_by}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700">{h.rows_accepted}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-500">{h.rows_skipped_duplicate}</td>
                    <td className="px-3 py-2 text-right font-mono text-red-700">{h.rows_rejected}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, color, label, value, testid }) => (
  <div className="border border-slate-200 rounded-md p-3 flex items-center gap-3" data-testid={testid}>
    <Icon className={`w-6 h-6 ${color}`} />
    <div><div className="text-xl font-bold font-mono">{value}</div><div className="text-xs text-slate-500">{label}</div></div>
  </div>
);
